import { forwardRef, ReactNode, useMemo } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { CallIntelTypes, CallIntelUtils } from '@frontend/api-call-intel';
import { NoTranscription } from '@frontend/assets';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { ChipVariants, NakedUl } from '@frontend/design-system';
import { transcribingCallLottie } from '../assets/lotties';
import { useCallIntelDefaultsShallowStore } from '../hooks';
import { CallInsightBaseProps } from '../types';
import { callStatusUtils } from '../utils';
import { CallEmptyState } from './empty-state/call-empty-state';
import { AnalyzingCallLoader } from './loaders/analyzing-call-loader';
import { PrivateFeature } from './private-feature';
import { TranscriptCard } from './transcript-card';

export interface CallTranscriptProps extends CallInsightBaseProps {
  highlightedCitation?: CallIntelTypes.HighlightedCitation;
  containerStyles?: Interpolation<Theme>;
  callFlags: ReturnType<typeof callStatusUtils.getCallFlags>;
}

const CallTranscriptWrapper = ({
  children,
  containerStyles,
}: {
  children: ReactNode;
  containerStyles?: Interpolation<Theme>;
}) => (
  <div css={[styles.containerStyles, containerStyles]} data-testid='call-transcript'>
    {children}
  </div>
);

export const CallTranscript = forwardRef<HTMLUListElement, CallTranscriptProps>(
  ({ call, callFlags, callMetadata, highlightedCitation, isLoading, containerStyles }, ref) => {
    const { t } = useTranslation('callIntelInsights');

    const { isFailedCall, isSkippedCall, isPrivateCall } = callFlags;

    const { dataLabels, headerChipVariants } = useCallIntelDefaultsShallowStore(
      'chipVariants',
      'dataLabels',
      'headerChipVariants'
    );

    const callParsedTranscripts = useMemo(
      () =>
        CallIntelUtils.parseTranscript(callMetadata?.transcript ?? '', {
          OFFICE_USER_FIRST_NAME: call?.officeUser?.firstName || call?.person?.firstName || 'John',
        }),
      [callMetadata?.transcript, call?.officeUser?.firstName, call?.person?.firstName]
    );

    const citations = useMemo(() => {
      return (callMetadata?.tasks || [])
        .map((task) => ({
          time: task.citationTime ?? '',
          text: task.citation ?? '',
          variant: (headerChipVariants[task.type].variant ?? 'neutral') as ChipVariants,
          popoverLabel: t(`{{taskType}} Task`, { taskType: dataLabels?.taskTypes?.[task.type] ?? task.type }),
        }))
        .filter((citation) => citation.time && citation.text && citation.variant)
        .sort((a, b) => b.text.length - a.text.length);
    }, [callMetadata, headerChipVariants, dataLabels, t]);

    const showLoading = callFlags.isProcessingCall || isLoading;
    const showEmptyState = !isPrivateCall && (isFailedCall || isSkippedCall || !callMetadata?.transcript);

    if (showLoading) {
      return (
        <CallTranscriptWrapper containerStyles={containerStyles}>
          <AnalyzingCallLoader lottie={transcribingCallLottie} message={t('Processing phone call...')} />
        </CallTranscriptWrapper>
      );
    }

    if (isPrivateCall) {
      return (
        <CallTranscriptWrapper containerStyles={containerStyles}>
          <PrivateFeature />
        </CallTranscriptWrapper>
      );
    }

    if (showEmptyState) {
      return (
        <CallTranscriptWrapper containerStyles={containerStyles}>
          <CallEmptyState
            mainText={t('No Transcript Available')}
            style={{ overflow: 'auto' }}
            subText={t('Please review the call recording.')}
            svg={NoTranscription}
          />
        </CallTranscriptWrapper>
      );
    }

    return (
      <CallTranscriptWrapper containerStyles={containerStyles}>
        <div css={styles.content}>
          <NakedUl css={styles.transcriptList} ref={ref}>
            {callParsedTranscripts.map((transcript) => (
              <TranscriptCard
                caller={call?.person}
                citations={citations}
                highlightedCitation={highlightedCitation}
                key={transcript.time.start}
                officeUser={call?.officeUser}
                time={transcript.time.start}
                speaker={transcript.speaker ?? ''}
                text={transcript.text ?? ''}
              />
            ))}
          </NakedUl>
        </div>
      </CallTranscriptWrapper>
    );
  }
);

CallTranscript.displayName = 'CallTranscript';

const styles = {
  containerStyles: css`
    display: flex;
    flex-direction: column;
    overflow: auto;
  `,
  content: css`
    align-items: baseline;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    overflow: auto;
    position: relative;
  `,
  transcriptList: css`
    display: flex;
    flex-direction: column;
    overflow: auto;
    padding-right: ${theme.spacing(2)};

    &::-webkit-scrollbar {
      -webkit-appearance: none;
      width: ${theme.spacing(1)};
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${theme.colors.neutral30};
      border-radius: ${theme.borderRadius.small};
    }
  `,
};
